export class HttpError extends Error {
  readonly status: number;
  readonly bodyText: string;
  readonly retryAfterSeconds: number | null;

  constructor(status: number, bodyText: string, retryAfterSeconds: number | null = null) {
    super(`HTTP ${status}`);
    this.name = 'HttpError';
    this.status = status;
    this.bodyText = bodyText;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

export interface FetchOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
  headers?: Record<string, string>;
  method?: string;
  body?: BodyInit;
  /** Status codes to retry (e.g. [502, 503, 504]). */
  retryOn?: number[];
  /** Total attempts including the first. */
  maxAttempts?: number;
  /** Backoff for attempt n (1-based) in ms. Default 500 * n, like USGSService. */
  backoffMs?: (attempt: number) => number;
}

function combineSignals(signal: AbortSignal | undefined, timeoutMs: number | undefined): AbortSignal | undefined {
  const signals: AbortSignal[] = [];
  if (signal) signals.push(signal);
  if (timeoutMs && timeoutMs > 0) signals.push(AbortSignal.timeout(timeoutMs));
  if (signals.length === 0) return undefined;
  if (signals.length === 1) return signals[0];
  return AbortSignal.any(signals);
}

export function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && (err.name === 'AbortError' || err.name === 'TimeoutError');
}

/** fetch with timeout, abort and optional transient-status retry. Returns the Response. */
export async function fetchWithRetry(url: string, options: FetchOptions = {}): Promise<Response> {
  const { retryOn = [], maxAttempts = 1, backoffMs = (n) => 500 * n } = options;
  const signal = combineSignals(options.signal, options.timeoutMs);
  let attempt = 1;
  for (;;) {
    const res = await fetch(url, { method: options.method, headers: options.headers, body: options.body, signal });
    if (!retryOn.includes(res.status) || attempt >= maxAttempts) return res;
    await new Promise((r) => setTimeout(r, backoffMs(attempt)));
    attempt++;
  }
}

export async function fetchJson<T>(url: string, options: FetchOptions = {}): Promise<T> {
  const res = await fetchWithRetry(url, options);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const retryAfter = res.headers.get('Retry-After');
    throw new HttpError(res.status, text, retryAfter ? Number(retryAfter) || null : null);
  }
  return (await res.json()) as T;
}

export async function fetchText(url: string, options: FetchOptions = {}): Promise<string> {
  const res = await fetchWithRetry(url, options);
  if (!res.ok) throw new HttpError(res.status, await res.text().catch(() => ''));
  return res.text();
}
